import React, { useState} from "react";
import {useEditor} from "../contexts/EditorContext";
import {toast} from "react-toastify";
import {Sketch} from '@uiw/react-color';
import {useUIModelApi} from "../contexts/UIModelApiContext";

type ThemeProps = {
    id?: string;
    projectId: string;
}
export type UIModelThemeModel = {
    theme: UIModelTheme;
}

export interface UIModelTheme {
    solidButtonBackground: string;
    solidButtonLabel: string;
    borderButtonBorder: string;
    borderButtonLabel: string;
    textButtonLabel: string;
    titleColor: string;
    subtitleColor: string;
    bodyTextColor: string;
    mainBackgroundColor: string;
    headerBackgroundColor: string;
    navigationBackgroundColor: string;
}

const ThemeEditor = ({id, projectId}: ThemeProps) => {
    const baseId = id || 'theme-editor';
    const {uiModel, theme, setTheme} = useEditor();
    const {updateTheme} = useUIModelApi();


    const updateThemeNetWork = async () => {
        try {
            const responseData = await updateTheme(projectId, uiModel?.id ?? '', theme);
            if (responseData) {
                toast.success("Updated Theme");
            }
        } catch (error) {
            console.log(error, 'Error');
        }
    }

    const [activeColorPicker, setActiveColorPicker] = useState<keyof UIModelTheme | null>(null);

    const handleColorChange = (key: keyof UIModelTheme, value: string) => {
        setTheme(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const ColorPicker = ({colorKey, label, value}: { colorKey: keyof UIModelTheme, label: string, value: string }) => {
        const isActive = activeColorPicker === colorKey;
        const pickerId = `${baseId}-color-picker-${colorKey}`;

        return (
            <div id={pickerId} data-testid={pickerId} className="tw-flex tw-items-center tw-space-x-3 tw-relative" onClick={(e) => e.stopPropagation()}>
                <div
                    id={`${pickerId}-swatch`}
                    className="tw-w-[48px] tw-h-[48px] tw-rounded tw-cursor-pointer tw-border tw-border-[#dadbdd]"
                    style={{backgroundColor: value}}
                    onClick={() => setActiveColorPicker(isActive ? null : colorKey)}
                />
                {isActive && (
                    <div id={`${pickerId}-palette`} className="tw-absolute tw-top-10 tw-left-0 tw-z-50">
                        <Sketch
                            color={value}
                            onChange={(color) => {
                                handleColorChange(colorKey, color.hex);
                            }}
                        />
                    </div>
                )}
                <div id={`${pickerId}-label-wrapper`} onClick={() => setActiveColorPicker(null)}>
                    <div id={`${pickerId}-label`} className="tw-justify-start tw-text-[#172b4d] tw-text-sm tw-font-normal tw-mb-2">{label}</div>
                    <div id={`${pickerId}-value`} className="tw-justify-start tw-text-[#6d788c] tw-text-sm tw-font-semibold">{value}</div>
                </div>
            </div>
        );
    };

    return (
        <div id={baseId} data-testid={baseId} className="tw-px-[80px] tw-pt-[26px] tw-w-full tw-overflow-auto" style={{scrollbarWidth: "none"}} onClick={() => setActiveColorPicker(null)}>
            <div id={`${baseId}-content`} className="tw-w-full">

                {/* Buttons Section */}
                <div id={`${baseId}-buttons-section`}>
                    <h2 id={`${baseId}-buttons-title`} className="tw-justify-start tw-text-[#172b4d] tw-text-lg tw-font-semibold tw-mb-6">Buttons</h2>

                    {/* Solid Button */}
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <button
                            className="tw-px-[22px] tw-py-[12px] tw-rounded tw-font-medium tw-text-sm tw-border-2"
                            style={{
                                backgroundColor: theme.solidButtonBackground,
                                color: theme.solidButtonLabel,
                                borderColor: theme.solidButtonBackground
                            }}
                        >
                            {'\xa0\xa0Solid Button\xa0\xa0'}
                        </button>
                        <div className="tw-ms-[82px] tw-flex tw-space-x-6">
                            <ColorPicker
                                colorKey="solidButtonBackground"
                                label="Button Color"
                                value={theme.solidButtonBackground}
                            />
                            <ColorPicker
                                colorKey="solidButtonLabel"
                                label="Label Color"
                                value={theme.solidButtonLabel}
                            />
                        </div>
                    </div>

                    {/* Border Button */}
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <button
                            className="tw-px-[22px] tw-py-[12px] tw-rounded tw-font-medium tw-text-sm tw-border-2 tw-bg-transparent"
                            style={{
                                borderColor: theme.borderButtonBorder,
                                color: theme.borderButtonLabel,
                            }}
                        >
                            Border Button
                        </button>
                        <div className="tw-ms-[82px]  tw-flex tw-space-x-6">
                            <ColorPicker
                                colorKey="borderButtonBorder"
                                label="Border Color"
                                value={theme.borderButtonBorder}
                            />
                            <ColorPicker
                                colorKey="borderButtonLabel"
                                label="Label Color"
                                value={theme.borderButtonLabel}
                            />
                        </div>
                    </div>

                    {/* Text Button */}
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <button
                            className="tw-px-[22px] tw-py-[12px] tw-rounded tw-font-medium tw-text-sm tw-bg-transparent tw-border-2 tw-me-[82px]"
                            style={{color: theme.textButtonLabel, borderColor: "#FFFFFFFF"}}
                        >
                            {'\xa0\xa0\xa0Text Button\xa0\xa0'}
                        </button>
                        <ColorPicker
                            colorKey="textButtonLabel"
                            label="Label Color"
                            value={theme.textButtonLabel}
                        />
                    </div>
                </div>

                <div id={`${baseId}-divider-1`} className="tw-w-full tw-h-[0.5px] tw-bg-[#d1d1d6] tw-my-[36px]" />

                {/* Text Section */}
                <div id={`${baseId}-text-section`} className="tw-w-[400px]">
                    <h2 id={`${baseId}-text-title`} className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-6">Text</h2>

                    {/* Title Text */}
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <div
                            className="tw-w-[225px] tw-text-lg tw-font-medium"
                            style={{color: theme.titleColor}}
                        >
                            Title Text
                        </div>
                        <ColorPicker
                            colorKey="titleColor"
                            label="Title Color"
                            value={theme.titleColor}
                        />
                    </div>

                    {/* Subtitle Text */}
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <div
                            className="tw-w-[225px] tw-text-base tw-font-normal"
                            style={{color: theme.subtitleColor}}
                        >
                            Subtitle Text
                        </div>
                        <ColorPicker
                            colorKey="subtitleColor"
                            label="Subtitle Color"
                            value={theme.subtitleColor}
                        />
                    </div>

                    {/* Body Text */}
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <div
                            className="tw-w-[225px] tw-text-base tw-font-normal"
                            style={{color: theme.bodyTextColor}}
                        >
                            Body Text
                        </div>
                        <ColorPicker
                            colorKey="bodyTextColor"
                            label="Body Text Color"
                            value={theme.bodyTextColor}
                        />
                    </div>
                </div>

                <div id={`${baseId}-divider-2`} className="tw-w-full tw-h-[0.5px] tw-bg-[#d1d1d6] tw-my-[36px]" />

                {/* Background Section */}
                <div id={`${baseId}-background-section`}>
                    <h2 id={`${baseId}-background-title`} className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-6">Background</h2>

                    <div className="tw-flex tw-items-center tw-mb-6">
                        <div className="tw-w-[225px] tw-text-base tw-font-normal tw-text-gray-700">
                            Main Bg
                        </div>
                        <ColorPicker
                            colorKey="mainBackgroundColor"
                            label="Bg Color"
                            value={theme.mainBackgroundColor}
                        />
                    </div>
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <div className="tw-w-[225px] tw-text-base tw-font-normal tw-text-gray-700">
                            Header Bg
                        </div>
                        <ColorPicker
                            colorKey="headerBackgroundColor"
                            label="Bg Color"
                            value={theme.headerBackgroundColor}
                        />
                    </div>
                    <div className="tw-flex tw-items-center tw-mb-6">
                        <div className="tw-w-[225px] tw-text-base tw-font-normal tw-text-gray-700">
                            Navigation Bg
                        </div>
                        <ColorPicker
                            colorKey="navigationBackgroundColor"
                            label="Bg Color"
                            value={theme.navigationBackgroundColor}
                        />
                    </div>
                </div>

                <button id={`${baseId}-save-btn`}
                        data-testid={`${baseId}-save-btn`}
                        onClick={async () => updateThemeNetWork()}
                        className='popup-button-filled tw-mt-4 tw-mb-6'>{'Save'}</button>
                <div id={`${baseId}-spacer`} className="tw-h-[200px]"/>
            </div>
        </div>
    );
};

export default ThemeEditor;